import { useEffect, useState } from 'react'
import { zxcvbnAsync, zxcvbnOptions } from '@zxcvbn-ts/core'
import { matcherPwnedFactory } from '@zxcvbn-ts/matcher-pwned'
import { debounce } from 'lodash'

export interface PasswordStrengthResult {
  score: number
  feedback: {
    warning: string
    suggestions: string[]
  }
  goodEnough: boolean
}

let dictionariesLoaded = false

async function loadDictionaries() {
  if (dictionariesLoaded) return

  const [zxcvbnCommonPackage, zxcvbnEnPackage, zxcvbnEsPackage] = await Promise.all([
    import('@zxcvbn-ts/language-common'),
    import('@zxcvbn-ts/language-en'),
    import('@zxcvbn-ts/language-es-es'),
  ])

  zxcvbnOptions.setOptions({
    dictionary: {
      ...zxcvbnCommonPackage.dictionary,
      ...zxcvbnEnPackage.dictionary,
      ...zxcvbnEsPackage.dictionary,
    },
    graphs: zxcvbnCommonPackage.adjacencyGraphs,
    translations: zxcvbnEsPackage.translations,
    useLevenshteinDistance: true,
    maxLength: 64,
  })

  dictionariesLoaded = true
}

zxcvbnOptions.setOptions({
  useLevenshteinDistance: true,
  maxLength: 64,
})

const matcherPwned = matcherPwnedFactory(fetch, zxcvbnOptions)

zxcvbnOptions.addMatcher('pwned', matcherPwned)

const checkPassword = debounce(async (password: string, setStrength: (strength: PasswordStrengthResult) => void) => {
  if (!password) {
    setStrength({
      score: 0,
      feedback: {
        warning: '',
        suggestions: [],
      },
      goodEnough: false,
    })

    return
  }

  if (password.length < 8) {
    setStrength({
      score: 0,
      feedback: {
        warning: 'La contraseña debe tener al menos 8 caracteres',
        suggestions: [],
      },
      goodEnough: false,
    })

    return
  }

  try {
    await loadDictionaries()

    const result = await zxcvbnAsync(password)

    const goodEnough = result.score >= 3

    setStrength({
      ...result,
      goodEnough,
    })
  } catch (error) {
    console.error('Error checking password strength:', error)
  }
}, 500)

export function usePasswordStrength(password: string): PasswordStrengthResult {
  const [strength, setStrength] = useState<PasswordStrengthResult>({
    score: 0,
    feedback: {
      warning: '',
      suggestions: [],
    },
    goodEnough: false,
  })

  useEffect(() => {
    checkPassword(password, setStrength)
  }, [password])

  return strength
}
